import Decimal from 'decimal.js';
import { compact, first, isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { NotificationSeverity } from '@/components/notifications/constants';
import { useFormContext } from '@/components/react-hook-form';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import {
  EstateWaterfallHypotheticalTransferTransferTaxKind,
  StateCode,
} from '@/types/schema';
import { maxDecimalJS } from '@/utils/decimalJSUtils';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { getNodes } from '@/utils/graphqlUtils';

import {
  HypotheticalTransferModalExemption_EstateWaterfallFragment,
  HypotheticalTransferModalExemptionQueryVariables,
  useHypotheticalTransferModalExemptionQuery,
} from '../graphql/HypotheticalTransferModalExemption.generated';
import { useHypotheticalTransferModalContext } from '../HypotheticalTransferModal.context';
import { HypotheticalTransferFormModalShape } from '../HypotheticalTransferModal.types';
import { deserializeTransferType } from '../HypotheticalTransferModal.utils';
import {
  getTotalHypotheticalTransferAmount,
  perGrantorNetHypotheticalTransferAmount,
} from './useHypotheticalTransferTypeHelpText';

export interface HypotheticalTransferCallout {
  severity: NotificationSeverity | null;
  message: string;
}

function isCTResident(stateCode: string | undefined | null): boolean {
  return stateCode === StateCode.Ct;
}

export function getCalloutData(
  estateWaterfall: HypotheticalTransferModalExemption_EstateWaterfallFragment,
  transferId: string,
  lifetimeExemptionAmount: Decimal,
  transferAmount: Decimal,
  transferType: HypotheticalTransferFormModalShape['transferType'],
  transferDiscount: HypotheticalTransferFormModalShape['taxDiscountRate'],
  sourceId: string
): HypotheticalTransferCallout {
  // Don't show callout until user has selected a transfer type
  if (!transferType) {
    return { severity: null, message: '' };
  }

  const { household, hypotheticalTransfers } = estateWaterfall;
  const { transferTaxKind, giftingGrantorIDs } =
    deserializeTransferType(transferType);
  const possiblePrimaryClients = household?.possiblePrimaryClients || [];

  // 1. Non-taxable gift
  if (
    transferTaxKind !==
    EstateWaterfallHypotheticalTransferTransferTaxKind.GrantorTaxableGift
  ) {
    return {
      severity: 'info-high',
      message:
        'No exemption will be used for this transfer and no gift tax will be assessed.',
    };
  }

  if (!transferAmount || transferAmount.lessThanOrEqualTo(0)) {
    return { severity: null, message: '' };
  }

  const grantors = compact(
    giftingGrantorIDs.map((grantorId) =>
      possiblePrimaryClients.find(({ id }) => id === grantorId)
    )
  );

  if (isEmpty(grantors)) return { severity: null, message: '' };

  // Check CT residency
  const areAllGrantorsCT = grantors.every((g) =>
    isCTResident(g.address?.stateCode)
  );

  const transfers = compact(getNodes(hypotheticalTransfers));

  const remainingExemptionByGrantor = grantors.map((grantor) => {
    const priorTransfersUsage = perGrantorNetHypotheticalTransferAmount({
      transfers,
      grantorId: grantor.id,
      transferId,
    });

    const remainingExemption = lifetimeExemptionAmount
      .minus(grantor.lifetimeExclusionUsed)
      .minus(priorTransfersUsage);

    return maxDecimalJS([remainingExemption], new Decimal(0));
  });

  // Check if ANY grantor triggers tax
  const isTaxTriggered = remainingExemptionByGrantor.some(
    (remainingExemption) => {
      // Calculate this grantor's share of the CURRENT transfer (taxable value)
      let currentTransferShare = transferAmount.div(grantors.length);
      if (transferDiscount) {
        const discount = currentTransferShare.times(transferDiscount.div(100));
        currentTransferShare = currentTransferShare.minus(discount);
      }

      return currentTransferShare.greaterThan(remainingExemption);
    }
  );

  if (isTaxTriggered) {
    // Find source entity value
    const sourceEntity = getNodes(household?.entities).find(
      (e) => e.id === sourceId
    );
    let sourceValue = new Decimal(0);

    if (sourceEntity?.subtype && 'currentValue' in sourceEntity.subtype) {
      sourceValue = new Decimal(sourceEntity.subtype.currentValue);
    }

    const taxEstimate = transferAmount.times(0.4);
    const totalNeeded = transferAmount.plus(taxEstimate);

    if (totalNeeded.greaterThan(sourceValue)) {
      return {
        severity: 'warning',
        message:
          'Transfer will result in gift tax that will be deducted from this entity in addition to the transfer amount. This entity does not contain enough assets to fund the transfer and the gift tax.',
      };
    }

    return {
      severity: 'info-high',
      message:
        'Transfer will result in gift tax that will be deducted from this entity in addition to the transfer amount.',
    };
  }

  // If not exhausted (Transfer covered by exemption)
  const exhaustionSentence =
    'If the transfer exhausts remaining exemption, the transfer will result in gift tax that will be deducted from this entity.';
  const totalRemainingExemption = formatCurrencyNoDecimals(
    Decimal.sum(...remainingExemptionByGrantor)
  );

  // 2. Both clients
  if (grantors.length === 2) {
    const names = grantors.map((g) => g.firstName).join(' and ');
    if (areAllGrantorsCT) {
      return {
        severity: 'info-high',
        message: `Transfer will be deducted from both ${names}’s remaining federal lifetime and state CT exemption (currently ${totalRemainingExemption} combined). ${exhaustionSentence}`,
      };
    } else {
      return {
        severity: 'info-high',
        message: `Transfer will be deducted from both ${names}’s remaining federal lifetime exemption (currently ${totalRemainingExemption} combined). ${exhaustionSentence}`,
      };
    }
  }

  // 3. One client
  if (grantors.length === 1) {
    const name = grantors[0]!.firstName;
    if (areAllGrantorsCT) {
      return {
        severity: 'info-high',
        message: `Transfer will be deducted from ${name}’s remaining federal lifetime and state CT exemption (currently ${totalRemainingExemption}). ${exhaustionSentence}`,
      };
    } else {
      return {
        severity: 'info-high',
        message: `Transfer will be deducted from ${name}’s remaining federal lifetime exemption (currently ${totalRemainingExemption}). ${exhaustionSentence}`,
      };
    }
  }

  return { severity: null, message: '' };
}

export function useHypotheticalTransferCallout(): HypotheticalTransferCallout {
  const { waterfallId, transferId = '' } =
    useHypotheticalTransferModalContext();
  const { control } = useFormContext<HypotheticalTransferFormModalShape>();

  const [
    transferAmount,
    transferType,
    transferStartYear,
    transferEndYear,
    transferFrequency,
    taxDiscountRate,
    sourceId,
  ] = useWatch({
    control,
    name: [
      'transferAmount',
      'transferType',
      'transferStartYear',
      'transferEndYear',
      'transferFrequency',
      'taxDiscountRate',
      'sourceId',
    ],
  });

  const variables = useMemo<HypotheticalTransferModalExemptionQueryVariables>(
    () => ({
      estateWaterfallWhere: { id: waterfallId },
    }),
    [waterfallId]
  );

  const { data } = useHypotheticalTransferModalExemptionQuery({
    variables,
    skip: !waterfallId,
  });

  const { lifetimeExemptionAmount } = useIRSConstants();
  const estateWaterfall = first(getNodes(data?.estateWaterfalls));

  const totalTransferAmount = useMemo(
    () =>
      getTotalHypotheticalTransferAmount({
        transferAmount,
        transferStartYear,
        transferEndYear,
        transferFrequency,
      }),
    [transferAmount, transferEndYear, transferFrequency, transferStartYear]
  );

  return useMemo(() => {
    if (!estateWaterfall || !lifetimeExemptionAmount) {
      return { severity: null, message: '' };
    }

    return getCalloutData(
      estateWaterfall,
      transferId,
      lifetimeExemptionAmount,
      totalTransferAmount,
      transferType,
      taxDiscountRate,
      sourceId
    );
  }, [
    estateWaterfall,
    lifetimeExemptionAmount,
    transferId,
    totalTransferAmount,
    transferType,
    taxDiscountRate,
    sourceId,
  ]);
}
